import { Button, Image, Spin } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import { memo, useEffect, useRef, useState } from "react"
import styled from "styled-components";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { useAtom, useAtomValue } from 'jotai';
import { isMapLoadedDoneAtom, showSamPanelAtom } from "../../stores/mapStore";
import { FaXmark, FaCamera } from "react-icons/fa6";
import { PiMagicWandBold } from "react-icons/pi";
import { IoEllipsisHorizontal } from "react-icons/io5";
import axios from "axios";

/**
 * 
 */
export const SamPanel = () => {
  const [showSamPanel, setShowSamPanel] = useAtom(showSamPanelAtom);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [points, setPoints] = useState<{ x: number; y: number; label: number }[]>([]);
  const [latLngs, setLatLngs] = useState<{ lat: number; lng: number }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMapLoadedDone, setMapLoadedDone] = useAtom(isMapLoadedDoneAtom);

  const captureScreenshot = async () => {
    if (!isMapLoadedDone) return;
    if (!window.map) return;
    const map = window.map!;

    setIsCapturing(true);

    // Wait for the next render frame to ensure the map is fully rendered
    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      // Create a new canvas with the same dimensions as the map
      const canvas = document.createElement('canvas');
      const mapCanvas = map.getCanvas();
      canvas.width = mapCanvas.width;
      canvas.height = mapCanvas.height;

      // Draw the map canvas to our new canvas
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      // Fill with white background first (in case of transparent areas)
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw the map
      ctx.drawImage(mapCanvas, 0, 0);

      // Convert to data URL and update state
      const dataUrl = canvas.toDataURL('image/png');
      setScreenshot(dataUrl);
    } catch (error) {
      console.error('Error capturing map screenshot:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  useEffect(() => {
    if (!window.map) return;
    const map = window.map!;
    const layerId = 'click-points-layer';
    const sourceId = 'click-points-source';
    if (map.getSource(sourceId) && map.getLayer(layerId)) {
      map.removeLayer(layerId);
      map.removeSource(sourceId);
    }

    if (latLngs.length > 0) {
      const sourceData = {
        type: 'FeatureCollection',
        features: latLngs.map(point => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.lng, point.lat],
          },
          properties: {},
        })),
      };
      map.addSource(sourceId, {
        type: 'geojson',
        data: sourceData,
      });
      map.addLayer({
        id: layerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': 10,
          'circle-color': '#007cbf',
        },
      });
    }
  }, [latLngs]);

  // Add click handler to map when component mounts
  useEffect(() => {
    if (!window.map) return;
    const map = window.map!;

    const handleMapClick = (event: maplibregl.MapMouseEvent) => {
      if (showSamPanel) {
        // Get pixel coordinates relative to the map container
        const pixel = event.point;
        console.log('Pixel position:', pixel);

        // Get the map's bounding rectangle to calculate relative position if needed
        const mapContainer = event.target.getCanvas().getBoundingClientRect();
        const relativeX = event.point.x - mapContainer.left;
        const relativeY = event.point.y - mapContainer.top;
        console.log('Relative to map container:', { x: relativeX, y: relativeY });

        if (points.length === 0) {
          captureScreenshot();
        }

        setPoints([...points, { x: pixel.x, y: pixel.y, label: 1 }]);
        setLatLngs([...latLngs, {
          lat: event.lngLat.lat,
          lng: event.lngLat.lng,
        }]);
      }
    };

    map.on('click', handleMapClick);

    return () => {
      map.off('click', handleMapClick);
    };
  }, [isMapLoadedDone, showSamPanel, points, latLngs]);

  const onClickStart = async () => {
    if (!window.map || !screenshot) {
      console.error('Map not initialized or no screenshot available');
      return [];
    }

    try {
      setIsProcessing(true);
      const response = await axios.post('https://sam.geodrone.tlgeo.net/generate-mask', {
        points: points,
        image: screenshot,
      });

      const screenPolygons = response.data.polygons;

      if (!Array.isArray(screenPolygons)) {
        console.error('Invalid polygon data received');
        return [];
      }

      // Function to convert screen coordinates to map coordinates
      const convertScreenToMapCoords = (screenCoords: number[][]): number[][] => {
        if (!window.map) return [];
        return screenCoords.map(point => {
          // Convert screen coordinates to map coordinates (lng, lat)
          const lngLat = window.map?.unproject([point[0], point[1]]);
          return [lngLat?.lng, lngLat?.lat];
        });
      };

      // Convert all polygons from screen to map coordinates
      const mapPolygons = screenPolygons.map((polygon: any) => {
        if (!Array.isArray(polygon) || polygon.length === 0) return [];

        // Handle both single polygon and multipolygon formats
        if (Array.isArray(polygon[0][0])) {
          // Multipolygon format
          return polygon.map((ring: number[][]) => convertScreenToMapCoords(ring));
        } else {
          // Single polygon format
          return convertScreenToMapCoords(polygon);
        }
      });

      console.log('Converted map coordinates:', mapPolygons);
      const features = mapPolygons.map((polygon: number[][]) => {
        return {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Polygon',
            coordinates: [polygon],
          },
        };
      });
      // const responsePoints: number[][] = mapPolygons[0] as number[][];
      // const geojsonPolygon = [...responsePoints, responsePoints[0]];

      // Add polygon as source, and add layer to map
      {
        const sourceId = 'polygon-source';
        const layerId = 'polygon-layer';
        const geojsonData = {
          type: 'FeatureCollection',
          features: features,
        };
        console.log('geojsonData', geojsonData);
        window.map?.addSource(sourceId, {
          type: 'geojson',
          data: geojsonData,
        });
        window.map?.addLayer({
          id: `${layerId}-fill`,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': '#ff0000',
            'fill-opacity': 0.2,
          },
        });
        window.map?.addLayer({
          id: `${layerId}-line`,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#ff0000',
            'line-width': 1,
          },
        });
      }

    } catch (error) {
      console.error('Error processing polygons:', error);
    } finally {
      setIsProcessing(false);
    }
  };


  return <UI_Wrapper>
    {/* Header */}
    <UI_Header>
      <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <PiMagicWandBold style={{ color: '#2563eb', fontSize: '18px' }} />
        <span>Tự động khoanh vùng (SAM)</span>
      </UI_HeaderLeft>
      <UI_HeaderRight>
        <UI_HeaderRight_item onClick={() => {
          setShowSamPanel(false);
        }}>
          <FaXmark />
        </UI_HeaderRight_item>
      </UI_HeaderRight>


    </UI_Header>
    {/* End Header */}
    {/* Toolbar */}
    <UI_Toolbar>
      {/* Left toolbar */}
      <UI_Toolbar_Left>
      </UI_Toolbar_Left>
      {/* End left toolbar */}

      {/* Right Toolbar */}
      <UI_Toolbar_Right>
        <UI_ToolbarItem>
          <IoEllipsisHorizontal />
        </UI_ToolbarItem>
      </UI_Toolbar_Right>
      {/* End right toolbar */}
    </UI_Toolbar>
    {/* End Toolbar */}
    {/* Content */}

    <UI_Content>
      {
        points.length > 0 && (
          <>
            <div>Đã chọn {points.length} điểm</div>
            <Button loading={isProcessing} onClick={() => {
              onClickStart();
            }}>Bắt đầu</Button>
          </>
        )
      }

      {/* <UI_ScreenshotContainer>
        {isCapturing ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        ) : screenshot ? (
          <Image
            src={screenshot || undefined}
            alt="Map Screenshot"
            style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
          />
        ) : (
          <UI_Placeholder>
            <FaCamera size={32} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <div>Nhấp vào bản đồ để chụp ảnh</div>
          </UI_Placeholder>
        )}
      </UI_ScreenshotContainer> */}
    </UI_Content>
  </UI_Wrapper>
}

export default memo(SamPanel);

const UI_Wrapper = styled.div`
  width: 300px;
  height: 500px;
  position: absolute;
  top: 16px;
  right: 16px;
  background-color: white;
  border-radius: 8px;
  background-color: rgba(255,255,255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadows-paneShadow);
`;

const UI_Content = styled.div`
  padding: 16px;
  height: calc(100% - 96px);
  overflow: auto;
`

const UI_Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const UI_ToolbarItem = styled.div`
  cursor: pointer;
  padding: 4px;
`

const UI_Toolbar_Left = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`

const UI_Toolbar_Right = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`

const UI_ScreenshotContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 4px;
  min-height: 200px;
`

const UI_Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  text-align: center;
  padding: 16px;
  height: 100%;
  width: 100%;
`